import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const CALL_LOGS_API = `${config.BASE_URL}/call-logs`;
const USER_SEARCH_API = `${CALL_LOGS_API}/users/search`;

async function parseJson(res) {
  const text = await res.text();
  if (text.trim().startsWith("<")) {
    return { message: "Server Error: An unexpected issue occurred." };
  }
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function normalizeMasterRows(payload, fallbackKeys = []) {
  const candidates = [payload?.data, payload?.rows, payload?.result, payload];
  const list = candidates.find((item) => Array.isArray(item)) || [];

  return list
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const label =
        row.type_name ||
        row.purpose_name ||
        row.status_name ||
        row.action_name ||
        fallbackKeys.map((key) => row[key]).find(Boolean) ||
        row.name ||
        row.label;

      return {
        ...row,
        id: row.id ?? row.value,
        label,
      };
    })
    .filter((row) => row?.id && row?.label);
}

function filterActiveMasterRows(rows) {
  return (rows || []).filter((row) => {
    if (!row) return false;
    if (Number(row.is_active) === 2) return false;
    const statusId = row.status_id != null ? Number(row.status_id) : 1;
    return statusId === 1;
  });
}

async function fetchMasterOptions(path, fallbackKeys = [], { activeOnly = true } = {}) {
  const res = await authFetch(`${CALL_LOGS_API}/${path}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || `Error fetching ${path}`);
  const rows = normalizeMasterRows(payload, fallbackKeys);
  return activeOnly ? filterActiveMasterRows(rows) : rows;
}

function buildParams(filters = {}) {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.search) params.append("search", filters.search);
  if (filters.caller_name) params.append("caller_name", filters.caller_name);
  if (filters.phone) params.append("phone", filters.phone);
  if (filters.handled_by) params.append("handled_by", filters.handled_by);
  if (filters.created_by !== undefined && filters.created_by !== null && filters.created_by !== "") {
    params.append("created_by", String(filters.created_by));
  }
  if (filters.is_call_completed !== undefined && filters.is_call_completed !== null && filters.is_call_completed !== "") {
    params.append("is_call_completed", String(filters.is_call_completed));
  }
  if (filters.call_type !== undefined && filters.call_type !== null && filters.call_type !== "") {
    params.append("call_type", String(filters.call_type));
  }
  if (filters.purpose_id) params.append("purpose_id", filters.purpose_id);
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.exact_call_date) params.append("exact_call_date", filters.exact_call_date);
  if (filters.call_time) params.append("call_time", filters.call_time);
  if (filters.exact_follow_up_date) params.append("exact_follow_up_date", filters.exact_follow_up_date);
  if (filters.remarks_contains) params.append("remarks_contains", filters.remarks_contains);
  if (filters.action_taken_contains) params.append("action_taken_contains", filters.action_taken_contains);
  if (filters.call_duration !== undefined && filters.call_duration !== null && filters.call_duration !== "") {
    params.append("call_duration", String(filters.call_duration));
  }
  if (filters.sort_by) params.append("sort_by", filters.sort_by);
  if (filters.sort_dir) params.append("sort_dir", filters.sort_dir);

  return params.toString();
}

export const getCallLogs = async (filters = {}) => {
  const q = buildParams(filters);
  const res = await authFetch(`${CALL_LOGS_API}${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Error fetching call logs");
  return data;
};

export const getCallLogById = async (id) => {
  const res = await authFetch(`${CALL_LOGS_API}/${id}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Error fetching call log details");
  return data;
};

export const exportCallLogs = async (filters = {}) => {
  const exportFilters = { ...filters };
  delete exportFilters.page;
  delete exportFilters.limit;
  delete exportFilters.sort_by;
  delete exportFilters.sort_dir;
  const q = buildParams(exportFilters);
  const res = await authFetch(`${CALL_LOGS_API}/export${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Export failed");
  return data;
};

export const patchCallLogFollowUp = async (id, follow_up_date, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/${id}/follow-up`, {
    method: "PATCH",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ follow_up_date: follow_up_date || null }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error updating follow-up");
  return payload;
};

export const getCallLogsStats = async (filters = {}) => {
  const q = buildParams(filters);
  const res = await authFetch(`${CALL_LOGS_API}/stats${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Error fetching call logs stats");
  return data;
};

export const createCallLog = async (callLogData, csrfToken) => {
  const res = await authFetch(CALL_LOGS_API, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(callLogData),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error creating call log");
  return payload;
};

export const updateCallLog = async (id, callLogData, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(callLogData),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error updating call log");
  return payload;
};

export const editCallLog = updateCallLog;

export const patchCallLogCompletion = async (id, isCompleted, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/${id}/status`, {
    method: "PATCH",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify({ is_call_completed: Boolean(isCompleted) }),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error updating call status");
  return payload;
};

export const deleteCallLog = async (id, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error deleting call log");
  return payload;
};

export const searchUsers = async (query) => {
  const params = new URLSearchParams();
  if (query) params.append("q", query);
  const res = await authFetch(`${USER_SEARCH_API}?${params.toString()}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.message || "Error searching users");
  return data;
};

export const getCallLogMasterData = async () => {
  const [purposes, actions] = await Promise.all([
    fetchMasterOptions("call-purposes", ["purpose_name"], { activeOnly: true }),
    fetchMasterOptions("action-taken", ["action_name"], { activeOnly: true }),
  ]);
  const types = [
    { id: 1, type_name: "Incoming", label: "Incoming", value: true },
    { id: 0, type_name: "Outgoing", label: "Outgoing", value: false },
  ];

  return { types, purposes, actions };
};

export const getCallTypes = async () => {
  return {
    data: [
      { id: 1, type_name: "Incoming", label: "Incoming", value: true },
      { id: 0, type_name: "Outgoing", label: "Outgoing", value: false },
    ],
  };
};

export const createCallType = async (data, csrfToken) => {
  throw new Error("Call type master removed: Incoming/Outgoing is fixed.");
};

export const updateCallType = async (id, data, csrfToken) => {
  throw new Error("Call type master removed: Incoming/Outgoing is fixed.");
};

export const deleteCallType = async (id, csrfToken) => {
  throw new Error("Call type master removed: Incoming/Outgoing is fixed.");
};

export const getCallPurposes = async () => {
  const rows = await fetchMasterOptions(
    "call-purposes?scope=master",
    ["purpose_name"],
    { activeOnly: false },
  );
  return { data: rows };
};

export const createCallPurpose = async (data, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/call-purposes`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(data),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error creating call purpose");
  return payload;
};

export const updateCallPurpose = async (id, data, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/call-purposes/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(data),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error updating call purpose");
  return payload;
};

export const deleteCallPurpose = async (id, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/call-purposes/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error deleting call purpose");
  return payload;
};

export const getActionTakenList = async () => {
  const rows = await fetchMasterOptions("action-taken", ["action_name"]);
  return { data: rows };
};

export const createActionTaken = async (data, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/action-taken`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(data),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error creating action");
  return payload;
};

export const updateActionTaken = async (id, data, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/action-taken/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(data),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error updating action");
  return payload;
};

export const deleteActionTaken = async (id, csrfToken) => {
  const res = await authFetch(`${CALL_LOGS_API}/action-taken/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const payload = await parseJson(res);
  if (!res.ok) throw new Error(payload.message || "Error deleting action");
  return payload;
};

async function triggerBrowserDownload(url, filename, filters = {}) {
  const q = buildParams(filters);
  const res = await authFetch(`${url}${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  if (!res.ok) {
    const payload = await parseJson(res);
    throw new Error(payload.message || "Export failed");
  }

  const blob = await res.blob();
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

export const exportCallLogsExcel = async (filters = {}) => {
  try {
    await triggerBrowserDownload(
      `${CALL_LOGS_API}/export/excel`,
      "call-logs.xlsx",
      filters
    );
  } catch {
    await triggerBrowserDownload(`${CALL_LOGS_API}/export`, "call-logs.xlsx", filters);
  }
};

export const exportCallLogsPdf = async (filters = {}) => {
  try {
    await triggerBrowserDownload(
      `${CALL_LOGS_API}/export/pdf`,
      "call-logs.pdf",
      filters
    );
  } catch {
    await triggerBrowserDownload(`${CALL_LOGS_API}/export`, "call-logs.pdf", filters);
  }
};
